import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Signal, TemplateRef, computed, inject, input, output, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';

import { RouteSegment } from '@core/enums/route-segment.enum';

import { WeatherDataStoreModel } from '@domain/multi-site-dashboard/multi-site-dashboard.models';

import { ChartComponent } from '@shared/components/chart/chart.component';
import {
  ChartConfig,
  ChartCursorTooltipTemplateContext,
  ChartDataPointTemplateContext,
  ChartType,
} from '@shared/components/chart/chart.view-model';
import { MovablePanelTitleComponent } from '@shared/components/movable-panel-title';
import { MoveablePanelComponent } from '@shared/components/moveable-panel/moveable-panel.component';
import { MultiSelectComponent, MultiSelectOptionViewModel } from '@shared/components/multi-select';
import { PanelLinkComponent } from '@shared/components/panel-link';
import { WeatherDisplayComponent } from '@shared/components/weather-display';
import { KwhPipe } from '@shared/pipes/kwh';
import { CurrencyFormatService } from '@shared/services/currency-format.service';
import { pickReadableForegroundClass } from '@shared/utils/color-utils';
import { DEFAULT_TIMEZONE } from '@shared/utils/locales';

import { EnergyCostRange } from '../../enums/energy-cost-range.enum';
import { EnergyCostOverviewMovablePanelLoadingSkeletonComponent } from '../energy-cost-overview-movable-panel-loading-skeleton';
import { EnergyCostOverviewLegendComponent } from './components/energy-cost-overview-legend';
import { EnergyCostOverviewChartDatum, EnergyCostOverviewMovablePanelViewModel } from './energy-cost-overview-movable-panel.view-model';

const ENERGY_COST_RANGE_OPTION_LABELS: Record<EnergyCostRange, string> = {
  [EnergyCostRange.Last7Days]: 'multi-site-dashboard.energy-cost-overview.last-7-days',
  [EnergyCostRange.Last14Days]: 'multi-site-dashboard.energy-cost-overview.last-14-days',
  [EnergyCostRange.Last30Days]: 'multi-site-dashboard.energy-cost-overview.last-30-days',
};

const X_AXIS_VERTICAL_LABELS_THRESHOLD = 7;

@Component({
  selector: 'app-energy-cost-overview-movable-panel',
  imports: [
    MoveablePanelComponent,
    MovablePanelTitleComponent,
    MultiSelectComponent,
    PanelLinkComponent,
    ChartComponent,
    EnergyCostOverviewLegendComponent,
    EnergyCostOverviewMovablePanelLoadingSkeletonComponent,
    FormsModule,
    RouterLink,
    TranslatePipe,
    WeatherDisplayComponent,
    DecimalPipe,
  ],
  providers: [DecimalPipe, KwhPipe],
  templateUrl: './energy-cost-overview-movable-panel.component.html',
  host: { class: 'flex flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnergyCostOverviewMovablePanelComponent {
  readonly #translateService = inject(TranslateService);
  readonly #kwhPipe = inject(KwhPipe);
  readonly #currencyFormatService = inject(CurrencyFormatService);

  public vm = input<EnergyCostOverviewMovablePanelViewModel>();
  public isLoading = input<boolean>(false);
  // Sourced separately from the store so the date-range select stays populated while the chart data
  // (the view model) is being reloaded.
  public range = input<EnergyCostRange>(EnergyCostRange.Last14Days);
  public rangeChange = output<EnergyCostRange>();
  public weatherCursorTooltipTemplate =
    viewChild<TemplateRef<ChartCursorTooltipTemplateContext<EnergyCostOverviewChartDatum>>>('weatherCursorTooltip');
  public weatherIconTemplate: Signal<TemplateRef<ChartDataPointTemplateContext<EnergyCostOverviewChartDatum>> | undefined> = viewChild(
    'weatherIcon',
    {
      read: TemplateRef,
    }
  );

  public readonly RouteSegment = RouteSegment;

  public readonly rangeOptions = computed((): MultiSelectOptionViewModel<EnergyCostRange>[] =>
    [EnergyCostRange.Last7Days, EnergyCostRange.Last14Days, EnergyCostRange.Last30Days].map((range) => ({
      value: range,
      label: this.#translateService.instant(ENERGY_COST_RANGE_OPTION_LABELS[range]),
    }))
  );

  public readonly chartConfig = computed((): ChartConfig<EnergyCostOverviewChartDatum> | undefined => {
    const vm = this.vm();
    if (!vm) {
      return undefined;
    }

    const { chartData, sites } = vm;
    const hasWeather = Object.values(vm.weatherByDateMillis).some((weather) => weather.icon || weather.temperatureCelsius !== null);

    return {
      data: chartData,
      margin: { top: 32, right: 10, bottom: 18, left: 16 },
      leftYAxis: {
        // The kWh scale must accommodate the full stack height, so map each day to its total energy.
        dataMapper: (data) => sites.reduce((sum, site) => sum + (data[site.id] ?? 0), 0),
        label: this.#translateService.instant('common.kwh'),
      },
      rightYAxis: {
        dataMapper: (data) => data.cost,
        label: this.#currencyFormatService.symbol(vm.currency),
      },
      xAxis: {
        dataKey: 'dateMillis',
        verticalTexts: chartData.length > X_AXIS_VERTICAL_LABELS_THRESHOLD,
        tickValues: chartData.map((datum) => datum.dateMillis),
        tickFormat: (value) => DateTime.fromMillis(value, { zone: DEFAULT_TIMEZONE }).toFormat('dd/MM'),
      },
      pointerPosition: {
        formatter: (value) => {
          return DateTime.fromMillis(value, { zone: DEFAULT_TIMEZONE }).toFormat('dd/MM');
        },
      },
      dataPointTemplates: hasWeather && this.weatherIconTemplate() ? [{ template: this.weatherIconTemplate()! }] : undefined,
      cursorTooltipTemplate:
        hasWeather && this.weatherCursorTooltipTemplate() ? { template: this.weatherCursorTooltipTemplate()! } : undefined,
      charts: [
        {
          type: ChartType.StackedBar,
          // `bars[0]` renders at the top of the stack, so reverse the site order to keep the legend
          // order while placing the first legend item at the bottom of each stack.
          bars: [...sites].reverse().map((site) => ({
            dataKey: site.id,
            fillColor: site.color,
            tooltipConfig: {
              fillColor: site.color,
              // Flip between primary/secondary text so the value stays readable on the bar's colour.
              textColorClass: pickReadableForegroundClass(site.color),
              formatter: (value) => this.#kwhPipe.transform(value),
            },
          })),
          maxBarWidth: 6,
          borderTopRadius: 0,
          borderBottomRadius: 0,
        },
        {
          type: ChartType.Line,
          dataKey: 'cost',
          rightYAxis: true,
          strokeColorClass: 'stroke-status-warning',
          strokeWidth: 3,
          tooltipConfig: {
            fillColorClass: 'fill-status-warning',
            textColorClass: 'fill-foreground-primary-100',
            formatter: (value) => this.#currencyFormatService.format(value, vm.currency),
          },
        },
      ],
    };
  });

  public onRangeChange(range: EnergyCostRange): void {
    this.rangeChange.emit(range);
  }

  public getWeather(data: EnergyCostOverviewChartDatum): WeatherDataStoreModel | undefined {
    return this.vm()?.weatherByDateMillis[data.dateMillis];
  }
}
